import type { ReactNode } from "react";
import { Link } from "wouter";
import { List, Search, FileText, Briefcase, FolderKanban, Wrench, Building2, ClipboardCheck } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { postHref } from "../lib/postNavigation";

export type DashboardListing = {
  type: "job" | "project" | "service" | "rfq" | "company" | "resume";
  kind: string;
  title: string;
  tab: string;
  id: string;
  hidden: boolean;
  status?: string;
  ownerUserId?: number | null;
  ownerClosed?: boolean;
  moderationHidden?: boolean;
  createdAt?: string;
};

/** Older owned-post transports omit creation metadata from their static types. */
export function listingCreatedAt(value: unknown): string | undefined {
  if (!value || typeof value !== "object" || !("createdAt" in value)) return undefined;
  return typeof value.createdAt === "string" && Number.isFinite(Date.parse(value.createdAt)) ? value.createdAt : undefined;
}

function listingHref(listing: DashboardListing): string {
  const routes = { job: "/j/", project: "/project/", service: "/s/", rfq: "/rfq/", company: "/c/", resume: "/p/" };
  return routes[listing.type] + encodeURIComponent(listing.id);
}

type Props = {
  legacyMobile?: boolean;
  isFeatured?: (listing: DashboardListing) => boolean;
  listings: readonly DashboardListing[];
  total: number;
  loading: boolean;
  readError: string | null;
  onRetry: () => void;
  error: string | null;
  query: string;
  kind: string;
  status: string;
  expanded: boolean;
  onQuery: (value: string) => void;
  onKind: (value: string) => void;
  onStatus: (value: string) => void;
  onExpand: () => void;
  renderStatus: (listing: DashboardListing) => ReactNode;
  renderActions: (listing: DashboardListing) => ReactNode;
};

function LegacyMobileListingCards(props: Props & { rows: readonly DashboardListing[]; kinds: readonly (readonly [string, string])[] }) {
  const { t } = useI18n();
  return <div data-mobile-section="listings" className="grid lg:grid-cols-3 gap-4 dashboard-listings">
    <section id="sec-listings" className="card p-5 lg:col-span-3 scroll-mt-20 dashboard-section">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-navy-50 text-navy grid place-items-center shrink-0"><FileText className="w-5 h-5" /></div>
          <div className="min-w-0"><h3 className="font-extrabold text-navy">{t("dashboard.listings.title")}</h3><p className="text-xs text-mut mt-0.5">{t("dashboard.listings.description")}</p></div>
        </div>
        <span className="chip bg-navy-50 text-navy-600 whitespace-nowrap mt-1">{props.loading || props.readError ? "—" : props.listings.length} {t("dashboard.listings.matching")}</span>
      </div>
      <div className="rounded-2xl border border-line bg-navy-50/40 p-3 mb-4">
        <div className="grid sm:grid-cols-[1fr_150px_140px] gap-2">
          <input className="field h-10 text-sm bg-white" value={props.query} onChange={(event) => props.onQuery(event.target.value)} placeholder={t("dashboard.listings.search")} aria-label={t("dashboard.listings.search")} />
          <select className="field h-10 text-sm bg-white" value={props.kind} onChange={(event) => props.onKind(event.target.value)} aria-label="Filter listing type">{props.kinds.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
          <select className="field h-10 text-sm bg-white" value={props.status} onChange={(event) => props.onStatus(event.target.value)} aria-label="Filter listing status">
            <option value="all">{t("dashboard.listings.allStatus")}</option><option value="live">{t("dashboard.status.live", "Live")}</option><option value="closed">{t("dashboard.status.closed", "Closed")}</option><option value="archived">{t("dashboard.status.archivedLabel", "Archived")}</option><option value="hidden">{t("dashboard.status.hidden", "Hidden")}</option>
          </select>
        </div>
      </div>
      {props.error && <p role="alert" className="text-sm text-brand-red mb-3">{props.error}</p>}
      {props.readError ? <p className="text-sm text-brand-red" role="alert">{props.readError} <button type="button" onClick={props.onRetry}>{t("common.retry", "Retry")}</button></p> : props.loading ? <p className="text-sm text-mut" role="status">{t("common.loading", "Loading…")}</p> : <div className="space-y-2">
        {props.rows.length === 0 && <div className="text-sm text-mut border border-dashed border-line rounded-lg p-6 text-center">
          {props.total === 0 ? <>{t("dashboard.listings.nothingPosted")} <Link href={postHref("job", "/dashboard")} className="text-brand-red font-semibold">{t("dashboard.focused.postJob")}</Link> {t("common.or", "or")} <Link href={postHref("service", "/dashboard")} className="text-brand-red font-semibold">{t("dashboard.focused.offerService")}</Link> {t("common.toGetStarted", "to get started.")}</> : t("dashboard.listings.noMatch")}
        </div>}
        {props.rows.map((listing) => {
          const unavailable = listing.hidden || listing.status === "closed" || (listing.type === "service" && listing.status !== "open");
          const Icon = listing.type === "job" ? Briefcase : listing.type === "project" ? FolderKanban : listing.type === "service" ? Wrench : listing.type === "company" ? Building2 : listing.type === "resume" ? FileText : ClipboardCheck;
          return <div key={listing.type + listing.id} className={`flex items-center justify-between border rounded-xl p-3 gap-2 sm:rounded-lg ${unavailable ? "border-line bg-navy-50/40" : "border-line bg-white"}`}>
            <Link href={listingHref(listing)} className="text-sm text-navy min-w-0 flex items-center gap-2 hover:text-brand-red">
              <span className="w-10 h-10 rounded-full bg-navy-50 text-navy grid place-items-center shrink-0"><Icon className="w-4.5 h-4.5" /></span>
              <span className="min-w-0"><span className="flex items-center gap-2"><span className="chip bg-navy-50 text-navy-600">{listing.kind}</span>{props.isFeatured?.(listing) && <span className="chip bg-brand-gold/15 text-[#8a5a00] shrink-0">Featured</span>}</span><span className="block font-semibold text-navy truncate mt-1">{listing.title}</span>{props.renderStatus(listing)}</span>
            </Link>
            {props.renderActions(listing)}
          </div>;
        })}
      </div>}
      {!props.loading && !props.readError && props.listings.length > 5 && <div className="mt-3 flex justify-end"><button type="button" onClick={props.onExpand} aria-expanded={props.expanded} className="text-sm font-semibold text-navy hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">{props.expanded ? t("common.showLess", "Show less") : t("common.showMore", "Show more")}</button></div>}
    </section>
  </div>;
}

export function DashboardListingTable(props: Props) {
  const { t, lang } = useI18n();
  const rows = props.expanded ? props.listings : props.listings.slice(0, 5);
  const kinds: [string, string][] = [
    ["all", t("dashboard.listings.allTypes")], ["job", t("dashboard.overview.jobs")],
    ["project", t("dashboard.overview.projects")], ["service", t("dashboard.overview.services")],
    ["rfq", "RFQ"], ["company", t("dashboard.companies.title")], ["resume", t("dashboard.overview.resumes")],
  ];
  if (props.legacyMobile) return <LegacyMobileListingCards {...props} rows={rows} kinds={kinds} />;
  return (
    <section id="sec-listings" className="dashboard-panel dashboard-listing-panel" aria-labelledby="dashboard-listings-title">
      <div className="dashboard-panel-heading">
        <h2 id="dashboard-listings-title"><List size={17} />{t("dashboard.listings.title")}</h2>
        <span className="dashboard-subtle">{props.loading || props.readError ? "—" : props.listings.length} {t("dashboard.listings.matching")}</span>
      </div>
      <div className="dashboard-listing-filters">
        <label className="dashboard-search"><Search size={15} /><input value={props.query} onChange={(event) => props.onQuery(event.target.value)} placeholder={t("dashboard.listings.search")} aria-label={t("dashboard.listings.search")} /></label>
        <select className="field" value={props.kind} onChange={(event) => props.onKind(event.target.value)} aria-label="Filter listing type">
          {kinds.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select className="field" value={props.status} onChange={(event) => props.onStatus(event.target.value)} aria-label="Filter listing status">
          <option value="all">{t("dashboard.listings.allStatus")}</option>
          <option value="live">{t("dashboard.status.live", "Live")}</option>
          <option value="closed">{t("dashboard.status.closed", "Closed")}</option>
          <option value="archived">{t("dashboard.status.archivedLabel", "Archived")}</option>
          <option value="hidden">{t("dashboard.status.hidden", "Hidden")}</option>
        </select>
      </div>
      {props.error && <p role="alert" className="dashboard-read-error">{props.error}</p>}
      {props.readError ? <p className="dashboard-read-error" role="alert">{props.readError} <button type="button" onClick={props.onRetry}>{t("common.retry", "Retry")}</button></p> : props.loading ? <p className="dashboard-empty" role="status">{t("common.loading", "Loading…")}</p> : rows.length === 0 ? (
        <div className="dashboard-empty">
          {props.total === 0 ? <>{t("dashboard.listings.nothingPosted")} <Link href={postHref("job", "/dashboard")}>{t("dashboard.focused.postJob")}</Link> {t("common.or", "or")} <Link href={postHref("service", "/dashboard")}>{t("dashboard.focused.offerService")}</Link></> : t("dashboard.listings.noMatch")}
        </div>
      ) : (
        <table className="dashboard-listing-table">
          <thead><tr><th>{t("dashboard.table.type", "Type")}</th><th>{t("dashboard.table.title", "Title")}</th><th>{t("dashboard.table.status", "Status")}</th><th>{t("dashboard.table.created", "Date created")}</th><th><span className="sr-only">{t("dashboard.table.actions", "Actions")}</span></th></tr></thead>
          <tbody>{rows.map((listing) => (
            <tr key={listing.type + listing.id}>
              <td className="dashboard-listing-kind"><span data-kind={listing.type}>{listing.kind}</span></td>
              <td className="dashboard-listing-title"><Link href={listingHref(listing)}>{listing.title}</Link></td>
              <td className="dashboard-listing-state">{props.renderStatus(listing)}</td>
              <td className="dashboard-listing-date">{listing.createdAt ? <time dateTime={listing.createdAt}>{new Date(listing.createdAt).toLocaleDateString(lang === "tl" ? "fil-PH" : "en-PH", { month: "short", day: "numeric", year: "numeric" })}</time> : <span aria-label={t("dashboard.dateUnavailable", "Date unavailable")}>—</span>}</td>
              <td className="dashboard-listing-actions">{props.renderActions(listing)}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {!props.loading && !props.readError && <div className="dashboard-table-footer">
        <span>{t("dashboard.showing", "Showing")} {rows.length} / {props.listings.length}</span>
        {props.listings.length > 5 && <button type="button" onClick={props.onExpand} aria-expanded={props.expanded}>{props.expanded ? t("common.showLess", "Show less") : t("common.showMore", "Show more")}</button>}
      </div>}
    </section>
  );
}
